import React, { useState } from "react";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import { RxCross2 } from "react-icons/rx";
import { MdContentCopy } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useGlobalContext } from "../../../../Contexts/GlobalContext";

const RedirectInfoStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 900,
  maxWidth: "90vw",
  maxHeight: "90vh",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 8,
  overflow: "auto",
};

const RedirectInfo = ({ open, onClose, surveyData, surveyId }) => {
  const navigate = useNavigate();
  const { organization } = useGlobalContext();
  const [copiedField, setCopiedField] = useState(null);

  const handleCopy = (text, fieldName) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const redirectEndpointBase = `${window.location.origin}/21df0c3543ff3bd23cecdaf921ff4a704a113d06dc1ec21045a186ad0f2f9a42`;

  const redirectTypes = [
    {
      name: "Complete",
      status: "10",
      description: "Respondent successfully completed the survey",
      color: "bg-green-50 border-green-200 text-green-800",
    },
    {
      name: "Terminate",
      status: "20",
      description: "Respondent was terminated from the survey",
      color: "bg-orange-50 border-orange-200 text-orange-800",
    },
    {
      name: "Quality Terminate",
      status: "30",
      description: "Respondent was terminated due to quality issues",
      color: "bg-red-50 border-red-200 text-red-800",
    },
    {
      name: "Quota Full",
      status: "40",
      description: "Survey quota has been reached",
      color: "bg-blue-50 border-blue-200 text-blue-800",
    },
  ];


  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
    >
      <Fade in={open}>
        <Box sx={RedirectInfoStyle}>
          <div className="w-full p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
              <h1 className="text-[#1a1a1a] text-[20px] font-[600]">
                Redirect Endpoints
              </h1>
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              >
                <RxCross2 size={18} className="text-gray-500" />
              </button>
            </div>

            {!organization?.key && (
              <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-gray-700 text-[14px] mb-3">
                  Generate a secret key to configure redirect endpoints.
                </p>
                <button
                  onClick={() => {
                    onClose();
                    navigate("/profile/redirects-and-keys");
                  }}
                  className="px-4 py-2 bg-[#0226BE] text-white rounded-md text-[14px] font-[500] hover:bg-[#021ea8] transition-colors"
                >
                  Go to Redirects & Keys
                </button>
              </div>
            )}

            {/* Endpoint Format */}
            {organization?.key && (
              <div className="mb-6">
                <p className="text-gray-600 text-[13px] mb-3">
                  Configure this endpoint in your system. Replace <code className="bg-gray-100 px-1 rounded text-[12px]">[ref_id]</code> with actual respondent ID and <code className="bg-gray-100 px-1 rounded text-[12px]">[status_code]</code> with the status.
                </p>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
                  <code className="text-[13px] font-mono text-gray-800 break-all block">
                    {redirectEndpointBase}?h={organization?.key}&id=[ref_id]&s=[status_code]
                  </code>
                </div>
              </div>
            )}

            {/* Status Codes Table */}
            <div className="mb-6">
              <h2 className="text-[#1a1a1a] text-[16px] font-[600] mb-3">
                Status Codes
              </h2>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-gray-700 text-[13px] font-[600]">Code</th>
                      <th className="text-left px-4 py-2.5 text-gray-700 text-[13px] font-[600]">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {redirectTypes.map((type, index) => {
                      const endpointUrl = organization?.key 
                        ? `${redirectEndpointBase}?h=${organization?.key}&id=[ref_id]&s=${type.status}`
                        : `${redirectEndpointBase}?h=[key]&id=[ref_id]&s=${type.status}`;
                      return (
                        <tr key={index} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-2.5">
                            <code className="text-[13px] font-mono text-[#0226BE] font-[600]">{type.status}</code>
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center justify-between">
                              <span className="text-gray-700 text-[14px]">{type.name}</span>
                              <button
                                onClick={() => handleCopy(endpointUrl, `copy-${index}`)}
                                className="ml-4 p-1.5 hover:bg-gray-200 rounded transition-colors"
                                title="Copy endpoint"
                              >
                                <MdContentCopy size={14} className={copiedField === `copy-${index}` ? "text-green-600" : "text-gray-500"} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Quick Link */}
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  onClose();
                  navigate("/profile/redirects-and-keys");
                }}
                className="text-[#0226BE] text-[14px] font-[500] hover:underline"
              >
                View full configuration →
              </button>
            </div>
          </div>
        </Box>
      </Fade>
    </Modal>
  );
};

export default RedirectInfo;
